import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { Product } from '#bv/data/products'
import { bundleFromPriceUsd } from '#bv/lib/productCatalog'
import { categories } from '#bv/data/products'
import { productImageUrl } from '#bv/lib/productImageUrl'
import { StarRatingPink } from '#bv/components/FeatureIcons'
import { BundleTierModal } from '#bv/components/BundleTierModal'
import { EliteBadge } from '#bv/components/EliteBadge'
import {
  ProductCardBlurbBlock,
  ProductCardFooter,
  ProductCardMetaRow,
} from '#bv/components/ProductCardParts'
import { useProductStock } from '#bv/lib/useProductStock'

interface ProductCardProps {
  product: Product
  style?: React.CSSProperties
  /** Dense grid card for Pulse Shop / shop catalog redesign. */
  variant?: 'default' | 'vault'
}

function categoryLabel(product: Product): string {
  return categories.find((c) => c.id === product.category)?.label ?? product.category
}

type BadgeConfig = { label: string; className: string }

function ProductBadgePill({
  badge,
  config,
  className = 'absolute top-3 left-3 z-[2]',
  size = 'sm',
  dense = false,
}: {
  badge: NonNullable<Product['badge']>
  config: BadgeConfig
  className?: string
  size?: 'sm' | 'md'
  dense?: boolean
}) {
  if (badge === 'elite') {
    return (
      <div className={className}>
        <EliteBadge size={size} />
      </div>
    )
  }
  const text = dense ? 'text-[0.65rem] font-bold' : 'text-xs font-semibold'
  return (
    <div className={`${className} rounded-md px-2.5 py-1 tracking-wide ${text} ${config.className}`}>
      {config.label}
    </div>
  )
}

function cardInnerVault(
  product: Product,
  badgeKey: Product['badge'],
  badge: BadgeConfig | null,
  isLowStock: boolean,
  bundleMin: number | undefined,
  productInStock: boolean,
) {
  const ratingText = product.rating.toFixed(1)
  const brand = categoryLabel(product)
  const tierCount = product.bundlePicker?.tiers.length
  const salePct =
    product.compareAtPrice != null && product.compareAtPrice > product.price
      ? Math.round((1 - product.price / product.compareAtPrice) * 100)
      : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="relative flex aspect-square w-full shrink-0 items-center justify-center overflow-hidden p-2.5"
        style={{
          background: 'linear-gradient(180deg, var(--black-3) 0%, var(--black-2) 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(0,191,255,0.08)',
        }}
      >
        <img
          src={productImageUrl(product.image)}
          alt={product.name}
          className="relative z-0 h-full w-full object-cover object-center transition-transform duration-300 ease-out select-none group-hover:scale-[1.05]"
          loading="lazy"
          decoding="async"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[22%]"
          style={{ background: 'linear-gradient(to top, rgba(7,5,8,0.6), transparent)' }}
        />
        {salePct != null ? (
          <div
            className="absolute top-3 left-3 z-[2] rounded-lg px-2.5 py-1.5 text-[0.72rem] font-extrabold uppercase tracking-wider shadow-[0_6px_24px_rgba(0,0,0,0.65)]"
            style={{
              background: 'linear-gradient(145deg, rgba(12,6,14,0.94) 0%, rgba(45,8,32,0.92) 55%, rgba(120,12,72,0.55) 100%)',
              border: '1.5px solid rgba(255,77,160,0.85)',
              color: '#f0f6ff',
              textShadow: '0 0 1px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.85), 0 0 12px rgba(0, 191, 255,0.45)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.12), 0 0 0 1px rgba(0,0,0,0.4), 0 4px 20px rgba(0, 191, 255,0.25)',
            }}
          >
            Sale −{salePct}%
          </div>
        ) : badgeKey && badge ? (
          <ProductBadgePill badge={badgeKey} config={badge} dense />
        ) : null}
        {productInStock && isLowStock ? (
          <div className="absolute top-3 right-3 z-[2] flex items-center gap-1.5 rounded-full border border-[rgba(0,191,255,0.28)] bg-black/50 px-2 py-1 text-[0.62rem] font-medium text-[#93c5fd] backdrop-blur-sm">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
            {product.stock} left
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4 pt-3.5">
        <ProductCardMetaRow label={brand} rating={ratingText} tierCount={tierCount} />

        <h3
          className="mt-2.5 line-clamp-2 font-display text-[0.95rem] font-semibold leading-snug tracking-tight transition-colors"
          style={{ color: 'var(--text-strong)' }}
          title={product.name}
        >
          {product.name}
        </h3>

        <ProductCardBlurbBlock product={product} />

        <ProductCardFooter
          product={product}
          productInStock={productInStock}
          bundleMin={bundleMin}
          ctaLabel={product.bundlePicker ? 'See tiers' : 'View'}
          size="sm"
        />
      </div>
    </div>
  )
}

function cardInnerDefault(
  product: Product,
  badgeKey: Product['badge'],
  badge: BadgeConfig | null,
  isLowStock: boolean,
  bundleMin: number | undefined,
  productInStock: boolean,
) {
  const ratingText = product.rating.toFixed(1)
  const brand = categoryLabel(product)
  const tierCount = product.bundlePicker?.tiers.length

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="relative flex aspect-square w-full shrink-0 items-center justify-center overflow-hidden p-2"
        style={{
          background: 'linear-gradient(180deg, var(--black-3) 0%, var(--black-2) 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(0,191,255,0.06)',
        }}
      >
        <img
          src={productImageUrl(product.image)}
          alt={product.name}
          className="relative z-0 h-full w-full object-cover object-center transition-transform duration-300 ease-out select-none group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[24%]"
          style={{ background: 'linear-gradient(to top, rgba(7,5,8,0.45), transparent)' }}
        />
        {badgeKey && badge ? <ProductBadgePill badge={badgeKey} config={badge} /> : null}
        {productInStock && isLowStock ? (
          <div className="absolute top-3 right-3 z-[2] flex items-center gap-1.5 rounded-full border border-[rgba(0,191,255,0.28)] bg-black/50 px-2.5 py-1 text-xs font-medium text-[#93c5fd] backdrop-blur-sm">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
            {product.stock} left
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-5">
        <ProductCardMetaRow label={brand} rating={ratingText} tierCount={tierCount} showRating={false} />

        <div className="mb-2 mt-2 flex shrink-0 items-center gap-2">
          <StarRatingPink rating={product.rating} size={14} />
          <span className="text-xs font-medium" style={{ color: 'var(--text-faint)' }}>
            {product.reviewCount.toLocaleString()} reviews
          </span>
        </div>

        <h3
          className="line-clamp-2 font-display text-base font-semibold leading-snug transition-colors"
          style={{ color: 'var(--text-strong)' }}
          title={product.name}
        >
          {product.name}
        </h3>

        <ProductCardBlurbBlock product={product} />

        <ProductCardFooter
          product={product}
          productInStock={productInStock}
          bundleMin={bundleMin}
          ctaLabel={product.bundlePicker ? 'See tiers' : 'View deal'}
          size="md"
        />
      </div>
    </div>
  )
}

function cardInner(
  product: Product,
  badgeKey: Product['badge'],
  badge: BadgeConfig | null,
  isLowStock: boolean,
  bundleMin: number | undefined,
  productInStock: boolean,
  variant: 'default' | 'vault',
) {
  if (variant === 'vault') {
    return cardInnerVault(product, badgeKey, badge, isLowStock, bundleMin, productInStock)
  }
  return cardInnerDefault(product, badgeKey, badge, isLowStock, bundleMin, productInStock)
}

export function ProductCard({ product, style, variant = 'default' }: ProductCardProps) {
  const [bundleOpen, setBundleOpen] = useState(false)
  const { inStock } = useProductStock()
  const badgeConfig = {
    bestseller: { label: 'Best Seller', className: 'badge-bestseller' },
    popular: { label: 'Popular', className: 'badge-popular' },
    new: { label: 'New', className: 'badge-new' },
    limited: { label: 'Limited', className: 'badge-limited' },
    recommended: { label: 'Recommended ⭐', className: 'badge-recommended' },
    elite: { label: 'Elite', className: 'badge-elite' },
  }

  const badgeKey = product.badge
  const badge = badgeKey ? badgeConfig[badgeKey] : null
  const isLowStock = product.stock <= 15
  const bundleMin = bundleFromPriceUsd(product)
  const productInStock = inStock(product.id)

  const outerVault =
    variant === 'vault'
      ? 'nv-vault-card rounded-2xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/40'
      : 'product-card glass rounded-2xl'

  const outerVaultStyle: React.CSSProperties | undefined = undefined

  if (product.bundlePicker) {
    return (
      <>
        <button
          type="button"
          onClick={() => setBundleOpen(true)}
          className={`${outerVault} group flex h-full min-h-0 w-full cursor-pointer flex-col overflow-hidden text-left`}
          style={{ ...style }}
        >
          {cardInner(product, badgeKey, badge, isLowStock, bundleMin, productInStock, variant)}
        </button>
        <BundleTierModal product={product} open={bundleOpen} onClose={() => setBundleOpen(false)} />
      </>
    )
  }

  return (
    <Link
      to="/products/$productId"
      params={{ productId: product.id.toString() }}
      className={`${outerVault} group flex h-full min-h-0 flex-col overflow-hidden`}
      style={{ ...style }}
    >
      {cardInner(product, badgeKey, badge, isLowStock, undefined, productInStock, variant)}
    </Link>
  )
}
